import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Alert, Breadcrumb, Card, DatePicker, Input, Select, Segmented, Spin, Typography, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { ArrowLeftOutlined, FolderOutlined, FileOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useGetPrimaveraTasksSmall,
  useGetTkfTaskActivityCodes,
  useVwTkfWbsHierarchy,
} from '../../../hooks/genericHooks';
import type {
  PrimaveraTaskSmall,
  VwTkfTaskActivityCode,
  VwTkfTaskActivityCodeTaskIdOnly,
  VwTkfWbsHierarchy,
} from '../../../types';
import StandardTable from '../../../components/common/StandardTable';
import ActivityDetailsShow from '../../../components/modals/ActivityDetailsShow';
import Button from '../../../components/common/Button';

const { RangePicker } = DatePicker;

type ExplorerRow =
  | {
      key: string;
      type: 'wbs';
      wbs: VwTkfWbsHierarchy;
    }
  | {
      key: string;
      type: 'activity';
      activity: PrimaveraTaskSmall;
    };

const normalizeRows = <T,>(resp: any): T[] => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.rows)) return resp.rows as T[];
  if (Array.isArray(resp?.recordset)) return resp.recordset as T[];
  return [];
};

const formatDateDMY = (v: any) => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD/MM/YYYY') : '';
};

const getActivityTargetRange = (a: PrimaveraTaskSmall) => {
  const startRaw = a.target_start_date;
  const endRaw = a.target_end_date;
  const start = startRaw ? dayjs(startRaw) : null;
  const end = endRaw ? dayjs(endRaw) : null;
  return {
    start: start && start.isValid() ? start : null,
    end: end && end.isValid() ? end : null,
  };
};

const activityMatchesDateRange = (
  a: PrimaveraTaskSmall,
  range: [any, any] | null,
  mode: 'within' | 'overlap'
): boolean => {
  if (!range || (!range[0] && !range[1])) return true;
  const fromTmp = range[0] ? dayjs(range[0]) : null;
  const toTmp = range[1] ? dayjs(range[1]) : null;
  const from = fromTmp && fromTmp.isValid() ? fromTmp.startOf('day') : null;
  const to = toTmp && toTmp.isValid() ? toTmp.endOf('day') : null;
  const { start, end } = getActivityTargetRange(a);

  // If no valid dates on activity, exclude it when a date filter is active
  if (!start && !end) return false;
  const s = start || end;
  const e = end || start;
  if (!s || !e) return false;

  if (mode === 'overlap') {
    // Intersection check: activity intersects [from, to]
    if (from && e.isBefore(from)) return false;
    if (to && s.isAfter(to)) return false;
    return true;
  }

  // Containment check: activity within [from, to]
  if (from && s.isBefore(from)) return false;
  if (to && e.isAfter(to)) return false;
  return true;
};

const durationDays = (start: any, end: any) => {
  if (!start || !end) return '';
  const s = dayjs(start);
  const e = dayjs(end);
  if (!s.isValid() || !e.isValid()) return '';
  const d = e.diff(s, 'day');
  return Number.isFinite(d) ? String(Math.max(0, d)) : '';
};

const durationDaysNum = (start: any, end: any) => {
  const s = durationDays(start, end);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const ToPlnProjSchedule: React.FC = () => {
  const { data: wbsResp, isLoading: wbsLoading, isError: wbsIsError, error: wbsError } = useVwTkfWbsHierarchy();
  const { data: taskResp, isLoading: taskLoading, isError: taskIsError, error: taskError } = useGetPrimaveraTasksSmall();

  const { data: taskActvCodesResp, isLoading: taskActvCodesLoading } = useGetTkfTaskActivityCodes();

  const wbsData = useMemo(() => normalizeRows<VwTkfWbsHierarchy>(wbsResp), [wbsResp]);
  const tasks = useMemo(
    () => normalizeRows<PrimaveraTaskSmall>(taskResp).filter(t => t.task_type !== 'TT_WBS'),
    [taskResp]
  );

  const [selectedWbsId, setSelectedWbsId] = useState<string | null>(null);
  const [treeSearch, setTreeSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);
  const [rightSearch, setRightSearch] = useState('');
  const [rightTaskCodeSearch, setRightTaskCodeSearch] = useState('');
  const [rightExclude, setRightExclude] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [activityDateRange, setActivityDateRange] = useState<any>(null);
  const [dateFilterMode, setDateFilterMode] = useState<'within' | 'overlap'>('within');
  const [selectedActvCodeTypeId, setSelectedActvCodeTypeId] = useState<number | null>(null);
  const [selectedActvCodeId, setSelectedActvCodeId] = useState<number | null>(null);
  const [treeFocusWbsId, setTreeFocusWbsId] = useState<string | null>(null);
  const [detailTaskCode, setDetailTaskCode] = useState<string | null>(null);
  const activityClickTimerRef = useRef<number | null>(null);
  const focusSnapshotRef = useRef<{
    treeSearch: string;
    selectedWbsId: string | null;
    expandedKeys: (string | number)[];
  } | null>(null);
  const [leftPaneWidth, setLeftPaneWidth] = useState<number>(340);
  const dragStateRef = useRef<{ dragging: boolean; startX: number; startWidth: number }>({
    dragging: false,
    startX: 0,
    startWidth: 340,
  });

  useEffect(() => {
    return () => {
      if (activityClickTimerRef.current != null) {
        window.clearTimeout(activityClickTimerRef.current);
      }
    };
  }, []);

  const focusActivityWbs = (activity: PrimaveraTaskSmall) => {
    const wid = String(activity.wbs_id ?? '');
    if (!wid) return;
    if (!treeFocusWbsId) {
      focusSnapshotRef.current = {
        treeSearch,
        selectedWbsId,
        expandedKeys,
      };
    }
    setTreeSearch('');
    setTreeFocusWbsId(wid);
    setSelectedWbsId(wid);
  };

  const resetRightFilters = () => {
    setRightSearch('');
    setRightTaskCodeSearch('');
    setRightExclude('');
    setActivityDateRange(null);
    setDateFilterMode('within');
    setTypeFilter([]);
    setSelectedActvCodeTypeId(null);
    setSelectedActvCodeId(null);
  };

  const resetRightFiltersKeepActivityCode = () => {
    setRightSearch('');
    setRightTaskCodeSearch('');
    setRightExclude('');
    setActivityDateRange(null);
    setDateFilterMode('within');
    setTypeFilter([]);
  };

  useEffect(() => {
    // Ensure Value resets when Type changes/clears
    setSelectedActvCodeId(null);
  }, [selectedActvCodeTypeId]);

  const allTaskActvCodes = useMemo(
    () => normalizeRows<VwTkfTaskActivityCode>(taskActvCodesResp),
    [taskActvCodesResp]
  );

  const allowedTaskIdSet = useMemo(() => {
    if (selectedActvCodeTypeId == null || selectedActvCodeId == null) return null;
    const s = new Set<string>();
    allTaskActvCodes.forEach(r => {
      if (Number(r.actv_code_type_id) !== Number(selectedActvCodeTypeId)) return;
      if (Number(r.actv_code_id) !== Number(selectedActvCodeId)) return;
      if (r.task_id != null) s.add(String(r.task_id));
    });
    return s;
  }, [allTaskActvCodes, selectedActvCodeTypeId, selectedActvCodeId]);

  const isActivityCodeFilterActive = selectedActvCodeTypeId != null && selectedActvCodeId != null;

  const activityMatchesCodeFilter = (a: PrimaveraTaskSmall) => {
    if (!allowedTaskIdSet) return true;
    return allowedTaskIdSet.has(String(a.task_id));
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    try {
      const wbsLen = wbsData.length;
      const taskLen = tasks.length;
      const wbsSample = wbsLen ? wbsData[0] : null;
      const taskSample = taskLen ? tasks[0] : null;
      // eslint-disable-next-line no-console
      console.debug('[ToPlnProjSchedule] fetch snapshot', {
        wbsLoading,
        wbsIsError,
        wbsError,
        wbsResp,
        wbsLen,
        wbsSample,
        taskLoading,
        taskIsError,
        taskError,
        taskResp,
        taskLen,
        taskSample,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug('[ToPlnProjSchedule] debug logging failed', e);
    }
  }, [wbsResp, taskResp, wbsLoading, taskLoading, wbsIsError, taskIsError, wbsError, taskError, wbsData, tasks]);

  const isExplicitRootParent = (parent: any) => parent == null || String(parent) === '0';

  const wbsIdSet = useMemo(() => {
    const s = new Set<string>();
    wbsData.forEach(w => s.add(String(w.wbs_id)));
    return s;
  }, [wbsData]);

  const isRootNode = (w: VwTkfWbsHierarchy) => {
    if (isExplicitRootParent(w.parent_wbs_id)) return true;
    const pid = String(w.parent_wbs_id);
    return !wbsIdSet.has(pid);
  };

  useEffect(() => {
    if (!selectedWbsId && wbsData.length) {
      const firstRoot = wbsData.find(w => isRootNode(w));
      if (firstRoot) {
        setSelectedWbsId(String(firstRoot.wbs_id));
        setExpandedKeys([String(firstRoot.wbs_id)]);
      }
    }
  }, [wbsData, selectedWbsId, wbsIdSet]);

  const wbsById = useMemo(() => {
    const m = new Map<string, VwTkfWbsHierarchy>();
    wbsData.forEach(w => m.set(String(w.wbs_id), w));
    return m;
  }, [wbsData]);

  const parentById = useMemo(() => {
    const m = new Map<string, string>();
    wbsData.forEach(w => {
      const id = String(w.wbs_id);
      const pid = String(w.parent_wbs_id);
      if (!isExplicitRootParent(w.parent_wbs_id) && wbsById.has(pid)) {
        m.set(id, pid);
      }
    });
    return m;
  }, [wbsData, wbsById]);

  const childrenByParent = useMemo(() => {
    const m = new Map<string, VwTkfWbsHierarchy[]>();
    wbsData.forEach(w => {
      const p = isRootNode(w) ? '__root__' : String(w.parent_wbs_id);
      const arr = m.get(p) || [];
      arr.push(w);
      m.set(p, arr);
    });
    // stable ordering by name
    m.forEach(arr => arr.sort((a, b) => String(a.wbs_name ?? '').localeCompare(String(b.wbs_name ?? ''))));
    return m;
  }, [wbsData, wbsIdSet]);

  const wbsNameSearchTokens = useMemo(() => treeSearch.toLowerCase().split(/\s+/).filter(Boolean), [treeSearch]);
  const wbsNameMatches = (w: VwTkfWbsHierarchy) => {
    if (!wbsNameSearchTokens.length) return true;
    const haystack = String(w.wbs_name ?? '').toLowerCase();
    return wbsNameSearchTokens.every(t => haystack.includes(t));
  };

  const treeSearchExpandKeys = useMemo(() => {
    if (!wbsNameSearchTokens.length) return null;

    // Find all matching nodes, then expand their ancestor chains
    const matchIds: string[] = [];
    wbsData.forEach(w => {
      if (wbsNameMatches(w)) matchIds.push(String(w.wbs_id));
    });

    const expand = new Set<string>();
    for (let i = 0; i < matchIds.length; i += 1) {
      let cur = matchIds[i];
      while (parentById.has(cur)) {
        const pid = parentById.get(cur) as string;
        expand.add(pid);
        cur = pid;
      }
    }
    return Array.from(expand);
  }, [wbsData, wbsNameSearchTokens, parentById]);

  const focusPathSet = useMemo(() => {
    if (!treeFocusWbsId) return null;
    const wid = String(treeFocusWbsId);
    const set = new Set<string>();
    set.add(wid);
    let cur = wid;
    while (parentById.has(cur)) {
      const pid = parentById.get(cur) as string;
      set.add(pid);
      cur = pid;
    }
    return set;
  }, [treeFocusWbsId, parentById]);

  useEffect(() => {
    if (!wbsNameSearchTokens.length) return;
    setExpandedKeys(treeSearchExpandKeys || []);
  }, [wbsNameSearchTokens, treeSearchExpandKeys]);

  useEffect(() => {
    if (!treeFocusWbsId) return;
    if (wbsNameSearchTokens.length) return;
    const wid = String(treeFocusWbsId);
    const ancestors: string[] = [];
    let cur = wid;
    while (parentById.has(cur)) {
      const pid = parentById.get(cur) as string;
      ancestors.push(pid);
      cur = pid;
    }
    setExpandedKeys(Array.from(new Set(ancestors)));
  }, [treeFocusWbsId, wbsNameSearchTokens]);

  useEffect(() => {
    if (wbsNameSearchTokens.length) {
      setTreeFocusWbsId(null);
    }
  }, [wbsNameSearchTokens.length]);

  const allExpandableWbsKeys = useMemo(() => {
    // Expand keys are the nodes which have children.
    const keys: string[] = [];
    wbsData.forEach(w => {
      const hasKids = (childrenByParent.get(String(w.wbs_id)) || []).length > 0;
      if (hasKids) keys.push(String(w.wbs_id));
    });
    return keys;
  }, [wbsData, childrenByParent]);

  const getTopmostAncestorId = (id: string) => {
    let cur = String(id);
    while (parentById.has(cur)) {
      cur = parentById.get(cur) as string;
    }
    return cur;
  };

  const getSubtreeExpandableKeys = (rootId: string) => {
    const out = new Set<string>();
    const stack: string[] = [String(rootId)];
    while (stack.length) {
      const cur = stack.pop() as string;
      const kids = childrenByParent.get(cur) || [];
      if (kids.length) out.add(cur);
      for (let i = 0; i < kids.length; i += 1) {
        stack.push(String(kids[i].wbs_id));
      }
    }
    return Array.from(out);
  };

  const getAncestorKeys = (id: string) => {
    const out: string[] = [];
    let cur = String(id);
    while (parentById.has(cur)) {
      const pid = parentById.get(cur) as string;
      out.push(pid);
      cur = pid;
    }
    return out;
  };

  const expandAllInSelection = () => {
    if (wbsNameSearchTokens.length) {
      setExpandedKeys(displayedTreeExpandableKeys);
      return;
    }
    if (selectedWbsId) {
      const subtree = getSubtreeExpandableKeys(String(selectedWbsId));
      const ancestors = getAncestorKeys(String(selectedWbsId));
      setExpandedKeys(Array.from(new Set([...ancestors, ...subtree])));
    } else {
      setExpandedKeys(allExpandableWbsKeys);
    }
  };

  const collapseAllToTop = () => {
    if (wbsNameSearchTokens.length) {
      setExpandedKeys(treeSearchExpandKeys || []);
      return;
    }
    setExpandedKeys([]);
    if (selectedWbsId) {
      const next = getTopmostAncestorId(String(selectedWbsId));
      if (next !== String(selectedWbsId)) {
        resetRightFiltersKeepActivityCode();
      }
      setSelectedWbsId(next);
    }
  };

  const tasksByWbs = useMemo(() => {
    const m = new Map<string, PrimaveraTaskSmall[]>();
    tasks.forEach(t => {
      const k = String(t.wbs_id ?? '');
      if (!k) return;
      const arr = m.get(k) || [];
      arr.push(t);
      m.set(k, arr);
    });
    m.forEach(arr => arr.sort((a, b) => String(a.task_code ?? '').localeCompare(String(b.task_code ?? ''))));
    return m;
  }, [tasks]);

  const selectedWbsSubtreeIds = useMemo(() => {
    if (!selectedWbsId) return null;
    const root = String(selectedWbsId);
    const out = new Set<string>();
    const stack: string[] = [root];
    while (stack.length) {
      const cur = stack.pop() as string;
      if (out.has(cur)) continue;
      out.add(cur);
      const kids = childrenByParent.get(cur) || [];
      for (let i = 0; i < kids.length; i += 1) {
        stack.push(String(kids[i].wbs_id));
      }
    }
    return out;
  }, [selectedWbsId, childrenByParent]);

  const candidateTaskIdSetForCascading = useMemo(() => {
    const tokens = rightSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const codeTokens = rightTaskCodeSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const excludeTokens = rightExclude.toLowerCase().split(/\s+/).filter(Boolean);
    const dateActive = !!(activityDateRange && (activityDateRange[0] || activityDateRange[1]));
    const allowedTypes = typeFilter.length ? new Set(typeFilter) : null;

    const subtreeIds = selectedWbsId ? selectedWbsSubtreeIds : null;

    const s = new Set<string>();
    tasks.forEach(a => {
      if (subtreeIds && !subtreeIds.has(String(a.wbs_id))) return;
      if (allowedTypes && !allowedTypes.has(String(a.task_type ?? ''))) return;
      const haystack = `${a.task_name ?? ''}`.toLowerCase();
      const codeHaystack = `${a.task_code ?? ''}`.toLowerCase();
      const includeOk = !tokens.length || tokens.every(t => haystack.includes(t));
      const codeOk = !codeTokens.length || codeTokens.every(t => codeHaystack.includes(t));
      const excludeOk = !excludeTokens.length || !excludeTokens.some(t => haystack.includes(t));
      if (!includeOk || !codeOk || !excludeOk) return;
      if (dateActive && !activityMatchesDateRange(a, activityDateRange, dateFilterMode)) return;
      s.add(String(a.task_id));
    });
    return s;
  }, [tasks, selectedWbsId, selectedWbsSubtreeIds, rightSearch, rightTaskCodeSearch, rightExclude, activityDateRange, typeFilter, dateFilterMode]);

  const activityCodeTypeOptions = useMemo(() => {
    const m = new Map<number, string>();
    allTaskActvCodes.forEach(r => {
      const tid = r.task_id != null ? String(r.task_id) : '';
      if (!tid || !candidateTaskIdSetForCascading.has(tid)) return;
      const id = r.actv_code_type_id != null ? Number(r.actv_code_type_id) : null;
      if (id == null || !Number.isFinite(id)) return;
      const label = String(r.actv_code_type ?? '').trim();
      if (!label) return;
      if (!m.has(id)) m.set(id, label);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [allTaskActvCodes, candidateTaskIdSetForCascading]);

  const activityCodeValueOptions = useMemo(() => {
    if (selectedActvCodeTypeId == null) return [];
    const m = new Map<number, string>();
    allTaskActvCodes.forEach(r => {
      const tid = r.task_id != null ? String(r.task_id) : '';
      if (!tid || !candidateTaskIdSetForCascading.has(tid)) return;
      if (Number(r.actv_code_type_id) !== Number(selectedActvCodeTypeId)) return;
      const id = r.actv_code_id != null ? Number(r.actv_code_id) : null;
      if (id == null || !Number.isFinite(id)) return;
      const code = String(r.actv_code ?? '').trim();
      if (!code) return;
      const label = `${code}${r.actv_name ? ` - ${String(r.actv_name)}` : ''}`;
      if (!m.has(id)) m.set(id, label);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [allTaskActvCodes, candidateTaskIdSetForCascading, selectedActvCodeTypeId]);

  // Inferred WBS ranges from descendant tasks (target dates only)
  const wbsAggRangeById = useMemo(() => {
    type R = { start: dayjs.Dayjs | null; end: dayjs.Dayjs | null };
    const direct: Map<string, R> = new Map();

    const merge = (a: R, b: R): R => {
      const s = a.start && b.start ? (a.start.isBefore(b.start) ? a.start : b.start) : a.start || b.start;
      const e = a.end && b.end ? (a.end.isAfter(b.end) ? a.end : b.end) : a.end || b.end;
      return { start: s || null, end: e || null };
    };

    // Seed direct ranges per WBS from tasks (target only)
    tasks.forEach(t => {
      const wid = String(t.wbs_id ?? '');
      if (!wid) return;
      const { start, end } = getActivityTargetRange(t);
      if (!start && !end) return;
      const s = start || end;
      const e = end || start;
      if (!s || !e) return;
      const cur = direct.get(wid) || { start: null, end: null };
      direct.set(wid, merge(cur, { start: s, end: e }));
    });

    const memo = new Map<string, R>();
    const visiting = new Set<string>();

    const dfs = (id: string): R => {
      const cached = memo.get(id);
      if (cached) return cached;
      if (visiting.has(id)) return { start: null, end: null };
      visiting.add(id);

      let agg: R = direct.get(id) || { start: null, end: null };
      const kids = childrenByParent.get(id) || [];
      for (let i = 0; i < kids.length; i += 1) {
        const childId = String(kids[i].wbs_id);
        agg = merge(agg, dfs(childId));
      }

      visiting.delete(id);
      memo.set(id, agg);
      return agg;
    };

    // Compute for all known WBS ids
    wbsData.forEach(w => {
      dfs(String(w.wbs_id));
    });

    return memo;
  }, [tasks, childrenByParent, wbsData]);

  const wbsHasMatchingActivityInSubtree = useMemo(() => {
    if (!allowedTaskIdSet) return null;

    const directMatchCount = new Map<string, number>();
    tasks.forEach(t => {
      const wid = String(t.wbs_id ?? '');
      if (!wid) return;
      if (!allowedTaskIdSet.has(String(t.task_id))) return;
      directMatchCount.set(wid, (directMatchCount.get(wid) || 0) + 1);
    });

    const memo = new Map<string, boolean>();
    const visiting = new Set<string>();
    const dfs = (id: string): boolean => {
      const cached = memo.get(id);
      if (cached != null) return cached;
      if (visiting.has(id)) return false;
      visiting.add(id);

      let has = (directMatchCount.get(id) || 0) > 0;
      const kids = childrenByParent.get(id) || [];
      for (let i = 0; i < kids.length; i += 1) {
        if (dfs(String(kids[i].wbs_id))) {
          has = true;
          break;
        }
      }

      visiting.delete(id);
      memo.set(id, has);
      return has;
    };

    wbsData.forEach(w => {
      dfs(String(w.wbs_id));
    });

    return memo;
  }, [allowedTaskIdSet, tasks, childrenByParent, wbsData]);

  const wbsNodeMatchesCodeFilter = (wbsId: string) => {
    if (!wbsHasMatchingActivityInSubtree) return true;
    return wbsHasMatchingActivityInSubtree.get(String(wbsId)) === true;
  };

  const buildTreeNodes = (parentKey: string): DataNode[] => {
    const kids = childrenByParent.get(parentKey) || [];
    return kids
      .filter(w => {
        if (!wbsNodeMatchesCodeFilter(String(w.wbs_id))) return false;
        if (treeFocusWbsId && !wbsNameSearchTokens.length && focusPathSet) {
          return focusPathSet.has(String(w.wbs_id));
        }
        if (!wbsNameSearchTokens.length) return true;
        // Keep node if it matches by name OR any descendant matches
        if (wbsNameMatches(w)) return true;
        const stack: VwTkfWbsHierarchy[] = [...(childrenByParent.get(String(w.wbs_id)) || [])];
        while (stack.length) {
          const cur = stack.pop() as VwTkfWbsHierarchy;
          if (!wbsNodeMatchesCodeFilter(String(cur.wbs_id))) continue;
          if (wbsNameMatches(cur)) return true;
          const more = childrenByParent.get(String(cur.wbs_id)) || [];
          for (let i = 0; i < more.length; i += 1) stack.push(more[i]);
        }
        return false;
      })
      .map(w => {
        const key = String(w.wbs_id);
        const isSelected = selectedWbsId != null && String(selectedWbsId) === key;
        return {
          key,
          title: (
            <span style={isSelected ? { color: '#1677ff', textDecoration: 'underline' } : undefined}>
              {w.wbs_name}
            </span>
          ),
          icon: <FolderOutlined />,
          children: w.has_children ? buildTreeNodes(key) : undefined,
        } as DataNode;
      });
  };

  const treeData: DataNode[] = useMemo(
    () => buildTreeNodes('__root__'),
    [childrenByParent, treeSearch, selectedWbsId, treeFocusWbsId, focusPathSet, wbsNameSearchTokens.length, wbsHasMatchingActivityInSubtree]
  );

  useEffect(() => {
    if (!wbsHasMatchingActivityInSubtree) return;

    // If current selection is no longer visible under the activity code filter, switch to the first visible root.
    if (selectedWbsId && wbsNodeMatchesCodeFilter(String(selectedWbsId))) return;

    const visibleRoots = wbsData.filter(w => isRootNode(w) && wbsNodeMatchesCodeFilter(String(w.wbs_id)));
    if (visibleRoots.length) {
      const nextId = String(visibleRoots[0].wbs_id);
      setSelectedWbsId(nextId);
      setExpandedKeys([nextId]);
    } else {
      setSelectedWbsId(null);
      setExpandedKeys([]);
    }
  }, [wbsHasMatchingActivityInSubtree, selectedWbsId, wbsData, wbsIdSet]);

  const displayedTreeExpandableKeys = useMemo(() => {
    const keys = new Set<string>();
    const stack: DataNode[] = [...treeData];
    while (stack.length) {
      const n = stack.pop() as DataNode;
      const children = (n as any).children as DataNode[] | undefined;
      if (children && children.length) {
        keys.add(String((n as any).key));
        for (let i = 0; i < children.length; i += 1) stack.push(children[i]);
      }
    }
    return Array.from(keys);
  }, [treeData]);

  const breadcrumbItems = useMemo(() => {
    const items: VwTkfWbsHierarchy[] = [];
    let cur = selectedWbsId ? wbsById.get(String(selectedWbsId)) : undefined;
    while (cur) {
      items.unshift(cur);
      const pid = String(cur.parent_wbs_id);
      cur = !isExplicitRootParent(cur.parent_wbs_id) && wbsById.has(pid) ? wbsById.get(pid) : undefined;
    }
    return items;
  }, [selectedWbsId, wbsById]);

  const baseExplorerRows: ExplorerRow[] = useMemo(() => {
    const tokens = rightSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const codeTokens = rightTaskCodeSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const excludeTokens = rightExclude.toLowerCase().split(/\s+/).filter(Boolean);
    const dateActive = !!(activityDateRange && (activityDateRange[0] || activityDateRange[1]));
    const textActive = tokens.length > 0 || codeTokens.length > 0;

    const globalActivityMatches = () =>
      tasks
        .filter(a => {
          const haystack = `${a.task_name ?? ''}`.toLowerCase();
          const codeHaystack = `${a.task_code ?? ''}`.toLowerCase();
          const includeOk = !tokens.length || tokens.every(t => haystack.includes(t));
          const codeOk = !codeTokens.length || codeTokens.every(t => codeHaystack.includes(t));
          const excludeOk = !excludeTokens.length || !excludeTokens.some(t => haystack.includes(t));
          return includeOk && codeOk && excludeOk && activityMatchesDateRange(a, activityDateRange, dateFilterMode) && activityMatchesCodeFilter(a);
        })
        .map(a =>
          ({
            key: `act:${a.task_id}`,
            type: 'activity',
            activity: a,
          }) as ExplorerRow
        );

    // If no WBS is selected, the right-side search becomes a global activity search.
    if (!selectedWbsId) {
      if (!textActive && !dateActive) {
        if (!allowedTaskIdSet) return [];
        return globalActivityMatches();
      }
      return globalActivityMatches();
    }

    const directWbsChildren = (childrenByParent.get(String(selectedWbsId)) || []).map(w =>
      ({
        key: `wbs:${w.wbs_id}`,
        type: 'wbs',
        wbs: w,
      }) as Extract<ExplorerRow, { type: 'wbs' }>
    );

    const directActivities = (tasksByWbs.get(String(selectedWbsId)) || []).map(a =>
      ({
        key: `act:${a.task_id}`,
        type: 'activity',
        activity: a,
      }) as Extract<ExplorerRow, { type: 'activity' }>
    );

    // Default explorer view: direct children folders + direct activities
    if (!textActive && !dateActive) {
      const filteredActivities = directActivities.filter(r => activityMatchesCodeFilter(r.activity));
      if (allowedTaskIdSet && filteredActivities.length === 0) {
        return globalActivityMatches();
      }
      let base = [...directWbsChildren, ...filteredActivities];
      return base;
    }

    // When searching/filtering (tokens and/or date range), filter folders by their own name, but search activities across the selected WBS subtree
    const folderMatches = directWbsChildren.filter(r => {
      if (r.type !== 'wbs') return false;
      const w = r.wbs;
      const haystack = `${w.wbs_name ?? ''}`.toLowerCase();
      const includeOk = !tokens.length || tokens.every(t => haystack.includes(t));
      const excludeOk = !excludeTokens.length || !excludeTokens.some(t => haystack.includes(t));
      return includeOk && excludeOk;
    });

    const subtreeIds = selectedWbsSubtreeIds;
    const activityMatches = tasks
      .filter(a => {
        if (subtreeIds && !subtreeIds.has(String(a.wbs_id))) return false;
        const haystack = `${a.task_name ?? ''}`.toLowerCase();
        const codeHaystack = `${a.task_code ?? ''}`.toLowerCase();
        const includeOk = !tokens.length || tokens.every(t => haystack.includes(t));
        const codeOk = !codeTokens.length || codeTokens.every(t => codeHaystack.includes(t));
        const excludeOk = !excludeTokens.length || !excludeTokens.some(t => haystack.includes(t));
        return includeOk && codeOk && excludeOk && activityMatchesDateRange(a, activityDateRange, dateFilterMode) && activityMatchesCodeFilter(a);
      })
      .map(a =>
        ({
          key: `act:${a.task_id}`,
          type: 'activity',
          activity: a,
        }) as ExplorerRow
      );

    if (allowedTaskIdSet && activityMatches.length === 0) {
      return globalActivityMatches();
    }

    return [...folderMatches, ...activityMatches];
  }, [selectedWbsId, childrenByParent, tasksByWbs, rightSearch, rightTaskCodeSearch, rightExclude, tasks, activityDateRange, selectedWbsSubtreeIds, allowedTaskIdSet, dateFilterMode]);

  const isGlobalActivityFallbackActive = useMemo(() => {
    if (!isActivityCodeFilterActive) return false;
    if (!selectedWbsId) return true;
    // If selected WBS has any matching activity in subtree, don't show fallback hint.
    if (!wbsHasMatchingActivityInSubtree) return false;
    return wbsHasMatchingActivityInSubtree.get(String(selectedWbsId)) !== true;
  }, [isActivityCodeFilterActive, selectedWbsId, wbsHasMatchingActivityInSubtree]);

  const typeOptions = useMemo(() => {
    const allTypes = new Set<string>();
    allTypes.add('Folder');
    tasks.forEach(t => {
      const v = String(t.task_type ?? '').trim();
      if (v) allTypes.add(v);
    });

    const counts = new Map<string, number>();
    for (const row of baseExplorerRows) {
      const k = row.type === 'wbs' ? 'Folder' : String(row.activity.task_type ?? '').trim();
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }

    return Array.from(allTypes)
      .filter(v => v)
      .sort((a, b) => a.localeCompare(b))
      .map(v => {
        const c = counts.get(v) || 0;
        return {
          label: `${v} (${c})`,
          value: v,
          disabled: c === 0,
        };
      });
  }, [baseExplorerRows, tasks]);

  const explorerRows = useMemo(() => {
    if (!typeFilter.length) return baseExplorerRows;
    const allowed = new Set(typeFilter);
    return baseExplorerRows.filter(r => {
      const v = r.type === 'wbs' ? 'Folder' : String(r.activity.task_type ?? '');
      return allowed.has(v);
    });
  }, [baseExplorerRows, typeFilter]);

  const columns: any[] = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 30,
      exportValue: (row: ExplorerRow) => (row.type === 'wbs' ? 'Folder' : String(row.activity.task_type ?? '')),
      render: (_: any, row: ExplorerRow) => (row.type === 'wbs' ? 'Folder' : String(row.activity.task_type ?? '')),
    },
    {
      title: 'Name',
      key: 'name',
      width: 320,
      exportValue: (row: ExplorerRow) => (row.type === 'wbs' ? row.wbs.wbs_name : row.activity.task_name),
      render: (_: any, row: ExplorerRow) =>
        row.type === 'wbs' ? (
          <span>
            <FolderOutlined style={{ marginRight: 12 }} />
            {row.wbs.wbs_name}
          </span>
        ) : (
          <span>
            <FileOutlined style={{ marginRight: 12 }} />
            {row.activity.task_name}
          </span>
        ),
    },
    {
      title: 'Code / ID',
      key: 'code',
      width: 50,
      exportValue: (row: ExplorerRow) =>
        row.type === 'wbs' ? String(row.wbs.wbs_id) : String(row.activity.task_code ?? row.activity.task_id),
      render: (_: any, row: ExplorerRow) =>
        row.type === 'wbs' ? String(row.wbs.wbs_id) : String(row.activity.task_code ?? row.activity.task_id),
    },
    {
      title: 'Start',
      key: 'start',
      width: 70,
      exportValue: (row: ExplorerRow) =>
        row.type === 'activity'
          ? formatDateDMY(row.activity.target_start_date)
          : formatDateDMY(wbsAggRangeById.get(String(row.wbs.wbs_id))?.start),
      sorter: (a: ExplorerRow, b: ExplorerRow) => {
        const aStart =
          a.type === 'activity'
            ? getActivityTargetRange(a.activity).start
            : wbsAggRangeById.get(String(a.wbs.wbs_id))?.start || null;
        const bStart =
          b.type === 'activity'
            ? getActivityTargetRange(b.activity).start
            : wbsAggRangeById.get(String(b.wbs.wbs_id))?.start || null;
        const at = aStart ? aStart.valueOf() : Number.NEGATIVE_INFINITY;
        const bt = bStart ? bStart.valueOf() : Number.NEGATIVE_INFINITY;
        return at - bt;
      },
      sortDirections: ['ascend', 'descend'],
      render: (_: any, row: ExplorerRow) =>
        row.type === 'activity'
          ? formatDateDMY(row.activity.target_start_date)
          : formatDateDMY(wbsAggRangeById.get(String(row.wbs.wbs_id))?.start),
    },
    {
      title: 'Finish',
      key: 'finish',
      width: 70,
      exportValue: (row: ExplorerRow) =>
        row.type === 'activity'
          ? formatDateDMY(row.activity.target_end_date)
          : formatDateDMY(wbsAggRangeById.get(String(row.wbs.wbs_id))?.end),
      sorter: (a: ExplorerRow, b: ExplorerRow) => {
        const aEnd =
          a.type === 'activity'
            ? getActivityTargetRange(a.activity).end
            : wbsAggRangeById.get(String(a.wbs.wbs_id))?.end || null;
        const bEnd =
          b.type === 'activity'
            ? getActivityTargetRange(b.activity).end
            : wbsAggRangeById.get(String(b.wbs.wbs_id))?.end || null;
        const at = aEnd ? aEnd.valueOf() : Number.NEGATIVE_INFINITY;
        const bt = bEnd ? bEnd.valueOf() : Number.NEGATIVE_INFINITY;
        return at - bt;
      },
      sortDirections: ['ascend', 'descend'],
      render: (_: any, row: ExplorerRow) =>
        row.type === 'activity'
          ? formatDateDMY(row.activity.target_end_date)
          : formatDateDMY(wbsAggRangeById.get(String(row.wbs.wbs_id))?.end),
    },
    {
      title: 'Days',
      key: 'days',
      width: 40,
      exportValue: (row: ExplorerRow) => {
        if (row.type === 'activity') {
          return durationDays(row.activity.target_start_date, row.activity.target_end_date);
        }
        const r = wbsAggRangeById.get(String(row.wbs.wbs_id));
        return durationDays(r?.start, r?.end);
      },
      sorter: (a: ExplorerRow, b: ExplorerRow) => {
        const aNum =
          a.type === 'activity'
            ? durationDaysNum(a.activity.target_start_date, a.activity.target_end_date)
            : durationDaysNum(wbsAggRangeById.get(String(a.wbs.wbs_id))?.start, wbsAggRangeById.get(String(a.wbs.wbs_id))?.end);
        const bNum =
          b.type === 'activity'
            ? durationDaysNum(b.activity.target_start_date, b.activity.target_end_date)
            : durationDaysNum(wbsAggRangeById.get(String(b.wbs.wbs_id))?.start, wbsAggRangeById.get(String(b.wbs.wbs_id))?.end);
        const av = aNum == null ? Number.NEGATIVE_INFINITY : aNum;
        const bv = bNum == null ? Number.NEGATIVE_INFINITY : bNum;
        return av - bv;

      },
      sortDirections: ['ascend', 'descend'],
      render: (_: any, row: ExplorerRow) => {
        if (row.type === 'activity') {
          return durationDays(row.activity.target_start_date, row.activity.target_end_date);
        }
        const r = wbsAggRangeById.get(String(row.wbs.wbs_id));
        return durationDays(r?.start, r?.end);
      },
    },
  ];

  const loading = wbsLoading || taskLoading || taskActvCodesLoading;
  const error = (wbsIsError ? wbsError : null) || (taskIsError ? taskError : null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragStateRef.current.dragging) return;
      const dx = e.clientX - dragStateRef.current.startX;
      const next = dragStateRef.current.startWidth + dx;
      const minW = 180;
      const maxW = 760;
      setLeftPaneWidth(Math.max(minW, Math.min(maxW, next)));
    };
    const onUp = () => {
      if (!dragStateRef.current.dragging) return;
      dragStateRef.current.dragging = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  return (
    <Card style={{ margin: 16 }} styles={{ body: { padding: 12 } }}>
      <Typography.Title level={4} style={{ margin: '0 0 8px 0' }}>
        Project Schedule (WBS Explorer)
      </Typography.Title>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Failed to load schedule data"
          description={(error as any)?.message || String(error)}
        />
      )}

      {!loading && !error && wbsData.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No WBS data returned"
          description="vw_TGF_wbs_hierarchy returned 0 rows for PPMTRAIN."
        />
      )}

      <Spin spinning={loading}>
        <div style={{ display: 'flex', gap: 12, minHeight: '70vh' }}>
          <div
            style={{
              width: leftPaneWidth,
              flex: '0 0 auto',
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <Input.Search
                placeholder="Search WBS..."
                allowClear
                value={treeSearch}
                onChange={e => setTreeSearch(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  onClick={expandAllInSelection}
                  style={{ flex: 1 }}
                  disabled={wbsNameSearchTokens.length > 0}
                >
                  Expand all
                </Button>
                <Button
                  size="small"
                  onClick={collapseAllToTop}
                  style={{ flex: 1 }}
                  disabled={wbsNameSearchTokens.length > 0}
                >
                  Collapse all
                </Button>
              </div>
            </div>

            <Tree
              showIcon
              blockNode
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={(keys: React.Key[]) => {
                if (wbsNameSearchTokens.length) return;
                setExpandedKeys(keys as any);
              }}
              selectedKeys={selectedWbsId ? [String(selectedWbsId)] : []}
              onSelect={(keys: React.Key[]) => {
                const k = keys && keys.length ? String(keys[0]) : null;
                if (k) {
                  setTreeFocusWbsId(null);
                  if (k !== String(selectedWbsId ?? '')) {
                    resetRightFiltersKeepActivityCode();
                  }
                  setSelectedWbsId(k);
                }
              }}
            />
          </div>

          <div
            onMouseDown={(e) => {
              dragStateRef.current.dragging = true;
              dragStateRef.current.startX = e.clientX;
              dragStateRef.current.startWidth = leftPaneWidth;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
            style={{
              width: 6,
              flex: '0 0 6px',
              cursor: 'col-resize',
              borderRadius: 6,
              background: '#f0f0f0',
            }}
            title="Drag to resize"
          />

          <div style={{ flex: '1 1 auto', border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff', overflow: 'hidden' }}>
            <div style={{ padding: 10, borderBottom: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Breadcrumb>
                {breadcrumbItems.length === 0 ? (
                  <Breadcrumb.Item>All Activities</Breadcrumb.Item>
                ) : (
                  breadcrumbItems.map(b => (
                    <Breadcrumb.Item key={String(b.wbs_id)}>
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedWbsId(String(b.wbs_id));
                          setExpandedKeys(prev => Array.from(new Set([...prev, String(b.wbs_id)])));
                        }}
                      >
                        {b.wbs_name}
                      </a>
                    </Breadcrumb.Item>
                  ))
                )}
              </Breadcrumb>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Input.Search
                  placeholder="Search folders/activities in this WBS..."
                  allowClear
                  value={rightSearch}
                  onChange={e => setRightSearch(e.target.value)}
                  style={{ flex: '1 1 200px' }}
                />
                <Input
                  placeholder="Task Code..."
                  allowClear
                  value={rightTaskCodeSearch}
                  onChange={e => setRightTaskCodeSearch(e.target.value)}
                  style={{ width: 160, flex: '0 0 auto' }}
                />
                <Select
                  placeholder="Activity Code Type"
                  value={selectedActvCodeTypeId ?? undefined}
                  allowClear
                  onChange={(v) => setSelectedActvCodeTypeId(v != null ? Number(v) : null)}
                  options={activityCodeTypeOptions}
                  style={{ width: 180, flex: '0 0 auto' }}
                  showSearch
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Select
                  placeholder="Activity Code Value"
                  value={selectedActvCodeId ?? undefined}
                  allowClear
                  onChange={(v) => setSelectedActvCodeId(v != null ? Number(v) : null)}
                  options={activityCodeValueOptions}
                  disabled={selectedActvCodeTypeId == null}
                  style={{ width: 180, flex: '0 0 auto' }}
                  showSearch
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Input
                  placeholder="Exclude..."
                  allowClear
                  value={rightExclude}
                  onChange={e => setRightExclude(e.target.value)}
                  style={{ width: 150, flex: '0 0 auto' }}
                />
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Type"
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(v as string[])}
                  options={typeOptions}
                  showSearch
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  style={{ minWidth: 120, flex: '0 0 auto' }}
                />
                <RangePicker
                  allowClear
                  value={activityDateRange}
                  onChange={(v) => setActivityDateRange(v as any)}
                  style={{ width: 220, flex: '0 0 auto' }}
                />
                <Segmented
                  value={dateFilterMode}
                  onChange={(v) => setDateFilterMode(v as 'within' | 'overlap')}
                  options={[
                    { label: 'Within', value: 'within' },
                    { label: 'Overlap', value: 'overlap' },
                  ]}
                  style={{ flex: '0 0 auto' }}
                />
                {treeFocusWbsId && !wbsNameSearchTokens.length && (
                  <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                      const snap = focusSnapshotRef.current;
                      setTreeFocusWbsId(null);
                      if (snap) {
                        setTreeSearch(snap.treeSearch);
                        setSelectedWbsId(snap.selectedWbsId);
                        setExpandedKeys(snap.expandedKeys);
                      }
                      focusSnapshotRef.current = null;
                    }}
                  >
                    Return
                  </Button>
                )}
              </div>
            </div>

            {isGlobalActivityFallbackActive && (
              <div style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
                <Typography.Text type="secondary">
                  Showing all activities matching the selected Activity Code filter.
                </Typography.Text>
              </div>
            )}

            <StandardTable<ExplorerRow>
              size="small"
              rowKey="key"
              columns={columns as any}
              dataSource={explorerRows}
              loading={loading}
              pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
              enableExport
              enableCellTooltips
              onRow={(row: ExplorerRow) => ({
                onClick: () => {
                  if (row.type !== 'activity') return;
                  if (activityClickTimerRef.current != null) {
                    window.clearTimeout(activityClickTimerRef.current);
                  }
                  const activity = row.activity;
                  activityClickTimerRef.current = window.setTimeout(() => {
                    activityClickTimerRef.current = null;
                    focusActivityWbs(activity);
                  }, 400);
                },
                onDoubleClick: () => {
                  if (row.type === 'wbs') {
                    const nextId = String(row.wbs.wbs_id);
                    setSelectedWbsId(nextId);
                    setExpandedKeys(prev => Array.from(new Set([...prev, nextId])));
                    return;
                  }
                  if (activityClickTimerRef.current != null) {
                    window.clearTimeout(activityClickTimerRef.current);
                    activityClickTimerRef.current = null;
                  }
                  const code = String(row.activity.task_code ?? '').trim();
                  if (code) setDetailTaskCode(code);
                  focusActivityWbs(row.activity);
                },
                style: { cursor: 'pointer' },
              })}
              style={{ margin: 0 }}
            />
          </div>
        </div>
      </Spin>
      <ActivityDetailsShow
        open={detailTaskCode != null}
        taskCode={detailTaskCode}
        onClose={() => setDetailTaskCode(null)}
      />
    </Card>
  );
};

export default ToPlnProjSchedule;
